import { useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import type { AlertConfigurationRule } from '@rtkwlf/rendall';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { hasIdenticalAlertEntry } from '../../../has-identical-alert-entry/has-identical-alert-entry';

import type { FormData } from './country-user-white-list-form-values';
import { toFormData, fromFormData } from './transform';
import { useCountryUserWhiteListValidation } from './use-country-user-white-list-validation/use-country-user-white-list-validation';

export const useCountryUserWhiteListForm = ({
  defaultValues,
  onSubmit,
  alert,
}: {
  defaultValues?: AlertConfigurationRule['data'];
  onSubmit: (newValues: AlertConfigurationRule['data']) => void;
  alert?: AlertConfigurationRule;
}) => {
  const validationSchema = useCountryUserWhiteListValidation();

  const handleSubmitTransformation: SubmitHandler<FormData> = (data) => {
    onSubmit(fromFormData(data));
  };

  const form = useForm<FormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: toFormData(defaultValues),
    resolver: yupResolver(validationSchema),
  });

  const formData = form.watch();
  const isDuplicate = useMemo(() => {
    return alert?.data && form.formState.isDirty
      ? hasIdenticalAlertEntry(alert.data, fromFormData(formData))
      : false;
  }, [alert?.data, formData, form.formState.isDirty]);

  return {
    control: form.control,
    handleSubmit: form.handleSubmit(handleSubmitTransformation),
    reset: form.reset,
    formState: form.formState,
    isDuplicate,
  };
};
